import { FC, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { SharingModal, SharingModalApi } from '#/components/Modal';
import { PageSpinner } from '#/components/Splash';
import { NotFound } from '#/components/Status';
import devtoolsApi from '#/dev/devtools';
import {
  useActions,
  useDataContext,
  usePageGuard,
  useResponsiveFormFactor,
} from '#/hooks';
import { PageRenderer } from '#/pages/PageRenderer';
import { MenuBackgroundStyle } from '#/service/constants';
import { remify } from '#/service/utils';
import { usePaywall } from '#/store/paywall';

import { PageContainerProps, PageControllerProps } from './pageTypes';
import { usePageController } from './usePageController';

/** Controller for Dynamic UI layout based pages/views. */
const PageController: FC<PageControllerProps> = (props) => {
  const { pageId, fullPage = true } = props;
  const formFactor = useResponsiveFormFactor();
  const { getPageContext } = useDataContext();
  const { isShowing: isPaywallShowing } = usePaywall();
  const sharingModalRef = useRef<SharingModalApi>(null);
  const { actions } = useActions(sharingModalRef);
  const {
    layout,
    isPageLoading,
    error,
    theme,
    translations,
    parentalSystem,
    v2Templates,
    defaultContext,
  } = usePageController(formFactor, props);
  usePageGuard();

  // prepare the params needed for the `buildDynamicUi` function, but don't call it yet
  // see the explanation in `PageRenderer.tsx`
  const buildDynamicUiParams = useMemo(
    () => ({
      fontConfig: theme.fonts,
      translations,
      parentalSystem,
      formFactor,
      v2Templates,
    }),
    [formFactor, parentalSystem, theme.fonts, translations, v2Templates]
  );

  const templateProps = layout?.templates?.[formFactor]?.properties;
  const hasTopPadding =
    (fullPage && theme.menu.background === MenuBackgroundStyle.Opaque) ||
    !!templateProps?.topContainerShouldNotOverlap;

  if (error) {
    if (error.statusCode === 404) {
      return <NotFound />;
    }

    throw error;
  }

  if (isPageLoading || !layout || isPaywallShowing) {
    return (
      <PageSpinner $startFadeout={isPageLoading} data-testid="page-spinner" />
    );
  }

  const context = getPageContext(defaultContext, layout);

  console.debug('PageController - Layout with data', layout);
  console.debug('PageController - v2Templates', v2Templates);

  if (process.env.goal === 'local') {
    // update devtools
    devtoolsApi.setLayout(layout);
    devtoolsApi.setContexts({ page: context });
    devtoolsApi.setStoryPageDef({
      storyTitle: 'Untitled story',
      layout,
      context,
      fontConfig: theme.fonts,
      translations,
      parentalSystem,
      formFactor,
      v2Templates,
      theme,
    });
  }

  return (
    <PageContainer $hasTopPadding={hasTopPadding}>
      <PageRenderer
        actions={actions}
        buildDynamicUiParams={buildDynamicUiParams}
        context={context}
        layout={layout}
        pageId={pageId}
      />
      <SharingModal apiRef={sharingModalRef} />
    </PageContainer>
  );
};

PageController.displayName = 'PageController';

export default PageController;

export const EmptyPage = () => <div />;

const PageContainer = styled.div<PageContainerProps>`
  padding-top: ${({ $hasTopPadding }) => ($hasTopPadding ? remify(80) : '0')};
  min-height: ${({ $hasTopPadding }) =>
    $hasTopPadding ? `calc(100vh - ${remify(80)})` : '100vh'};
`;
